import React, { useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts';
import { HoldingViewModel, HoldingDiversification } from '@/store/holdingsApi';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatUtcDate } from '@/utils/date';
import { convertNumberToPercentage } from '@/utils/numbers';
import { mobileWidth } from '@/constants';
import { useGraphColors } from '@/hooks/useGraphColors';
import { useTranslation } from 'react-i18next';

interface HoldingDiversificationChartProps {
  holding: HoldingViewModel;
  diversificationData?: HoldingDiversification;
  asOfDate?: string;
  isLoading?: boolean;
  hasError?: boolean;
}

const PieChartSkeleton = () => (
  <Stack
    direction='column'
    gap={4}
    alignItems='center'
  >
    <Skeleton
      variant='rectangular'
      width={300}
      height={300}
    />
  </Stack>
);

const HoldingDiversificationChart: React.FC<
  HoldingDiversificationChartProps
> = ({ holding, diversificationData, asOfDate, isLoading, hasError }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileMatch = useMediaQuery(mobileWidth);
  const isTabletMatch = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const graphColors = useGraphColors();
  const records = (diversificationData as any)?.Records ?? [];

  // Responsive chart dimensions - progressively larger as viewport increases
  const chartWidth = isMobileMatch ? 300 : isTabletMatch ? 400 : 400;
  const chartHeight = isMobileMatch ? 400 : isTabletMatch ? 500 : 500;
  const outerRadius = isMobileMatch ? 150 : isTabletMatch ? 200 : 200;
  const arcLabelRadius = isMobileMatch ? 40 : isTabletMatch ? 40 : 40;
  const centerX = chartWidth / 2;
  const centerY = isMobileMatch
    ? chartHeight / 2
    : isTabletMatch
      ? chartHeight / 2
      : 250;

  const currentValues = useMemo(() => {
    const map: Record<string, number> = {};
    records.forEach((item: any) => {
      if (item.name && typeof item.y === 'number') {
        if (!map[item.name]) map[item.name] = 0;
        map[item.name] += item.y;
      }
    });
    return map;
  }, [records]);

  const currentPieData = useMemo(() => {
    const total = Object.values(currentValues).reduce((a, b) => a + b, 0);
    return Object.entries(currentValues).map(([name, value]) => ({
      id: name,
      label: name,
      value,
      formattedValue: formatNumberToCurrency(value),
      percentageLabel:
        total > 0 ? convertNumberToPercentage(value / total) : '0%',
    }));
  }, [currentValues]);

  const currentPieDataWithColor = useMemo(
    () =>
      currentPieData.map((d, index) => ({
        ...d,
        color: graphColors[index % graphColors.length],
      })),
    [currentPieData, graphColors],
  );

  const totalValue = Object.values(currentValues).reduce((a, b) => a + b, 0);

  if (isLoading) return <PieChartSkeleton />;
  if (hasError) return <Box>Error loading allocation data</Box>;

  return (
    <Stack
      direction='column'
      gap={4}
      alignItems='center'
    >
      <Box
        sx={{
          textAlign: 'center',
          width: '100%',
          maxWidth: 500,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography variant='h6'>{holding.Name} Allocation</Typography>
        {asOfDate && (
          <Typography variant='body2'>
            {t('dateFormats.asOfUtcDate', {
              date: formatUtcDate(new Date(asOfDate)),
            })}
          </Typography>
        )}
        <Typography
          variant='body2'
          sx={{ mb: 2 }}
        >
          Total Value: {formatNumberToCurrency(totalValue)}
        </Typography>
        <Box
          className='holding-diversification-chart'
          sx={{
            width: '100%',
            height: chartHeight,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            [`& .${pieArcLabelClasses.root}`]: {
              fill: 'white',
              fontSize: { xs: 12, sm: 14 },
              fontWeight: 'bold',
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
            },
            '& .MuiChartsLegend-root': { display: 'none' },
            '& svg': {
              width: '100%',
              maxWidth: `${chartWidth}px`,
              height: 'auto',
            },
          }}
        >
          <PieChart
            height={chartHeight}
            width={chartWidth}
            series={[
              {
                data: currentPieDataWithColor.map((d) => ({
                  id: d.id,
                  value: d.value,
                  label: `${d.percentageLabel}`,
                  color: d.color,
                })),
                valueFormatter: (v) => {
                  if (!v || typeof v.value === 'undefined') return '';
                  return formatNumberToCurrency(
                    v.value as number,
                    undefined,
                    0,
                  );
                },
                arcLabel: (item) =>
                  `${(((item.value as number) / totalValue) * 100).toFixed(1)}%`,
                arcLabelMinAngle: 25,
                arcLabelRadius: arcLabelRadius,
                highlightScope: { fade: 'global', highlight: 'item' },
                outerRadius: outerRadius,
                innerRadius: 0,
                paddingAngle: 1,
                cornerRadius: 5,
                startAngle: 0,
                endAngle: 360,
                cx: centerX,
                cy: centerY,
              },
            ]}
            margin={{ top: 40, bottom: 60, left: 0, right: 0 }}
          />
        </Box>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          mt={2}
          width='100%'
        >
          {currentPieDataWithColor.map((d) => (
            <Box
              key={d.label}
              display='flex'
              alignItems='center'
              width='100%'
              maxWidth={500}
              sx={{ cursor: 'default', p: 1 }}
            >
              <Box
                width={12}
                height={12}
                borderRadius='50%'
                bgcolor={d.color}
                mr={1}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { sm: 'center' },
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {d.label}
                </Typography>
                <Typography sx={{ ml: { sm: 2 } }}>
                  {d.formattedValue} ({d.percentageLabel})
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Stack>
  );
};

export default HoldingDiversificationChart;
